/*
In-memory index of every Paris tree, used by the cluster-recolour
enrichment pass and by the species mini-map filter.

Why a separate fetch + parse instead of asking MapLibre:
- MapLibre's clustered GeoJSON source exposes querySourceFeatures(), but
  with cluster=true that returns clusters at low zoom and only renders
  tile-loaded features. We need a global iteration over every tree
  regardless of viewport (e.g. to build the species-filtered FC).
- The browser HTTP / SW cache already holds the geojson from the map
  load, so the second fetch is essentially instant — only the parse
  cost (~0.3-1 s desktop, ~1-2 s on a phone) is paid, and only once.

We keep ONLY the lightweight `fFeatures` array (id, sk, remarquable, lat,
lon) — roughly 213k × 5 fields ≈ 25-30 MB. The full parsed GeoJSON
FeatureCollection is dropped right after we project it; iOS Safari
Standalone WebContent is killed around ~200-400 MB on most iPhones, and
keeping a second 50-100 MB object tree on top of MapLibre's own copy
caused the page to go white after a few captures (each capture's setData
rebuilds the cluster index, churning peak memory on top of the steady-
state load). When `applyDiscoveryToFeatures` needs a fresh FC for
setData, we rebuild it from `fFeatures` and let it die after the push.
*/

const kAssetPath = '/data/arbres-paris.geojson'  // served by the Pages Function in pwa/functions/data/, which streams the pre-gzipped sibling with Content-Encoding: gzip — browser auto-decodes

export interface ArbreFeature
{
	id: number
	speciesIndex: number
	remarquable: boolean
	lat: number
	lon: number
}

interface RawFeature
{
	geometry: { coordinates: [number, number] }
	properties: {
		id: number
		sk: number
		remarquable: boolean
		discovered?: number
	}
}

interface RawCollection
{
	type: 'FeatureCollection'
	features: RawFeature[]
}

let fFeatures: ArbreFeature[] | null = null  // memoized lightweight array (id/sk/remarquable/lat/lon), null until first parse
let fLoading: Promise<ArbreFeature[]> | null = null  // in-flight loader, dedup concurrent callers

/*
Ensure the trees array is loaded and parsed. Idempotent. Concurrent
callers share a single fetch+parse via fLoading. The full parsed
FeatureCollection is consumed and dropped — only the lightweight
`fFeatures` projection survives the call (see file header for why).
*/
export async function ensureArbresFeatures(): Promise<ArbreFeature[]>
{
	if (fFeatures) return fFeatures
	if (fLoading) return fLoading
	fLoading = (async () => {
		const vResp = await fetch(kAssetPath)  // SW serves cache-first; second hit after the map load is free
		if (!vResp.ok)
		{
			fLoading = null
			throw new Error(`${kAssetPath}: HTTP ${vResp.status}`)
		}
		const vJson = await vResp.json() as RawCollection  // parsed GeoJSON, scoped to this call so it can be GC'd
		const vOut: ArbreFeature[] = new Array(vJson.features.length)  // pre-size for the linear copy
		for (let vI = 0; vI < vJson.features.length; vI++)
		{
			const vRaw = vJson.features[vI]
			vOut[vI] = {
				id: vRaw.properties.id,
				speciesIndex: vRaw.properties.sk,
				remarquable: vRaw.properties.remarquable === true,
				lon: vRaw.geometry.coordinates[0],
				lat: vRaw.geometry.coordinates[1],
			}
		}
		fFeatures = vOut
		fLoading = null
		return vOut
	})()
	return fLoading
}


/*
Build a transient FeatureCollection that stamps every feature's
`properties.discovered` flag (0 or 1) from the user's capture set. The
caller hands this directly to MapLibre's `setData`, at which point
MapLibre rebuilds the cluster index (so `discovered_count` updates the
cluster bucket colours) and we drop our reference — letting the FC be
GC'd in the next idle.

Returns null when `fFeatures` isn't loaded yet (caller should defer).
Cost: one allocation of 213k Feature objects + a linear scan, ~80-150 ms
on a phone. Higher peak than the previous in-place mutation, but no
permanent retention — keeping the FC pinned in memory is what was
killing iOS Safari Standalone after a few captures.
*/
export function applyDiscoveryToFeatures(inCapturedIds: Set<number>): GeoJSON.FeatureCollection | null
{
	if (!fFeatures) return null
	const vOut: GeoJSON.Feature[] = new Array(fFeatures.length)
	for (let vI = 0; vI < fFeatures.length; vI++)
	{
		const vF = fFeatures[vI]
		vOut[vI] = {
			type: 'Feature',
			geometry: { type: 'Point', coordinates: [vF.lon, vF.lat] },
			properties: {
				id: vF.id,
				sk: vF.speciesIndex,
				remarquable: vF.remarquable,
				discovered: inCapturedIds.has(vF.id) ? 1 : 0,
			},
		}
	}
	return { type: 'FeatureCollection', features: vOut }
}

/*
Build a stand-alone FeatureCollection containing only the trees of the
given species, plus a precomputed [west, south, east, north] bbox to
fitBounds on.

Used by the species detail mini-map. Avoids loading the full source
into a second MapLibre instance — the filtered subset is typically
< 5000 features (and < 1 MB JSON), so we hand it off as inline data.
*/
export function featuresBySpecies(inSpeciesIndex: number): {
	collection: GeoJSON.FeatureCollection
	bbox: [number, number, number, number]
} | null
{
	if (!fFeatures) return null
	const vMatching: GeoJSON.Feature[] = []  // built into a fresh FC
	let vWest = Infinity  // running bbox edges
	let vSouth = Infinity
	let vEast = -Infinity
	let vNorth = -Infinity
	for (const vF of fFeatures)
	{
		if (vF.speciesIndex !== inSpeciesIndex) continue
		vMatching.push({
			type: 'Feature',
			geometry: { type: 'Point', coordinates: [vF.lon, vF.lat] },
			properties: { id: vF.id, remarquable: vF.remarquable },
		})
		if (vF.lon < vWest) vWest = vF.lon
		if (vF.lon > vEast) vEast = vF.lon
		if (vF.lat < vSouth) vSouth = vF.lat
		if (vF.lat > vNorth) vNorth = vF.lat
	}
	if (vMatching.length === 0) return null
	return {
		collection: { type: 'FeatureCollection', features: vMatching },
		bbox: [vWest, vSouth, vEast, vNorth],
	}
}
